import { format, parse } from 'date-fns';
import React, { useState } from 'react';

import { CONSTANTS } from '../../../core/config/constants';
import { DateRangeFilter } from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import CalendarPickerInput from '../../Inputs/CalendarPicker/CalendarPickerInput';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import SwitchCompaniesUnknowns from '../../Inputs/SwitchCompaniesUnknowns';

interface Props {
  root: boolean;
  withUnknownSwitch?: boolean;
  unknownSwitchEnabled?: boolean;
  onChange: (value: DateRangeFilter) => void;
  defaultValue?: DateRangeFilter;
  active: boolean;
}

const DateRangeFilterInput: React.FC<Props> = React.memo(
  ({
    onChange,
    withUnknownSwitch = false,
    unknownSwitchEnabled,
    root,
    active,
    defaultValue = withUnknownSwitch
      ? {
          includeEmpty: unknownSwitchEnabled === undefined ? true : unknownSwitchEnabled,
          value: {
            from: '',
            to: '',
          },
        }
      : {
          value: {
            from: '',
            to: '',
          },
        },
  }) => {
    const [state, setState] = useState<DateRangeFilter>(defaultValue);

    let defaultDatePickerValue;

    if (state.value?.from && state.value?.to) {
      defaultDatePickerValue = [
        parse(state.value.from, CONSTANTS.DATE_FORMATS.API, new Date()),
        parse(state.value.to, CONSTANTS.DATE_FORMATS.API, new Date()),
      ];
    }

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    const rs: any[] = [];
    if (withUnknownSwitch && state.includeEmpty) {
      rs.push({ label: 'Included:', value: 'Companies with unknown data' });
    }
    if (state.value?.from && state?.value?.to) {
      rs.push({ label: 'Range:', value: `From ${state.value.from} to ${state.value.to}` });
    }

    return (
      <>
        {active ? (
          <>
            {withUnknownSwitch && (
              <SwitchCompaniesUnknowns
                onChange={(value) => {
                  setState({ ...state, includeEmpty: value });
                }}
                checked={state.includeEmpty}
              />
            )}
            <CalendarPickerInput
              isRange={true}
              maxDate={new Date()}
              defaultValue={defaultDatePickerValue}
              onChange={(date) => {
                if (date && Array.isArray(date)) {
                  setState({
                    ...state,
                    value: {
                      from: format(date[0], CONSTANTS.DATE_FORMATS.API),
                      to: format(date[1], CONSTANTS.DATE_FORMATS.API),
                    },
                  });
                } else {
                  if (typeof date === 'undefined') {
                    setState({
                      ...state,
                      value: {
                        from: '',
                        to: '',
                      },
                    });
                  } else {
                    setState(defaultValue);
                  }
                }
              }}
            />
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

export default DateRangeFilterInput;
